import "./auth-native-app-download.scss";

import { useState } from "react";
import { light } from "@fortawesome/fontawesome-svg-core/import.macro";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import EnvelopeIcon from "assets/images/v2/envelope.svg";
import NoLOIcon from "assets/images/v2/loan-officer-silhouette.svg";
import { QrBlock, useNativeAppPromoState } from "components/v2/native-app-promo-modal/NativeAppPromoModal";
import useDesktopChecker from "hooks/useDesktopChecker";
import { LoanOfficerDetail } from "pages/application/applicationService";
import {
  AUTH_NATIVE_APP_APP_STORE_URL,
  AUTH_NATIVE_APP_DOWNLOAD_SUBTITLE,
  AUTH_NATIVE_APP_DOWNLOAD_TITLE,
  AUTH_NATIVE_APP_GOOGLE_PLAY_URL,
  NATIVE_APP_PROMO_APP_STORE_BADGE_URL,
  NATIVE_APP_PROMO_GET_APP_BUTTON_LABEL,
  NATIVE_APP_PROMO_GOOGLE_PLAY_BADGE_URL,
  NATIVE_APP_PROMO_POINT_CAMERA_HELPER,
  NATIVE_APP_PROMO_SCAN_TO_DOWNLOAD,
} from "utilities/Constant";
import { GLOBAL_LINK_NO_TX_CLASS } from "utilities/globalLinkNoTx";
import { getLoDisplayFields } from "utilities/loanOfficerDisplay";
import { formatPhoneNumber } from "utilities/string";

const storeBadges = [
  {
    href: AUTH_NATIVE_APP_APP_STORE_URL,
    label: "Download on the App Store",
    badgeSrc: NATIVE_APP_PROMO_APP_STORE_BADGE_URL,
  },
  {
    href: AUTH_NATIVE_APP_GOOGLE_PLAY_URL,
    label: "Get it on Google Play",
    badgeSrc: NATIVE_APP_PROMO_GOOGLE_PLAY_BADGE_URL,
  },
] as const;

export interface AuthNativeAppDownloadPanelProps {
  readonly loanOfficer?: LoanOfficerDetail | null;
  readonly onPhoneClick?: () => void;
  readonly onEmailClick?: () => void;
  readonly onHeadshotError?: () => void;
}

const openNativeAppStore = () => {
  const isAndroid = /android/i.test(navigator.userAgent);
  window.open(isAndroid ? AUTH_NATIVE_APP_GOOGLE_PLAY_URL : AUTH_NATIVE_APP_APP_STORE_URL, "_blank");
};

export default function AuthNativeAppDownloadPanel({
  loanOfficer,
  onPhoneClick,
  onEmailClick,
  onHeadshotError,
}: AuthNativeAppDownloadPanelProps) {
  const isDesktop = useDesktopChecker();
  const { isQrOk, onQrError } = useNativeAppPromoState();
  const [imageLoadError, setImageLoadError] = useState(false);
  const isWithLo = !!loanOfficer;
  const { title, email, phone } = getLoDisplayFields(loanOfficer);
  const phoneNumber = phone || loanOfficer?.phoneNumber;

  const badges = (
    <div className="auth-native-app-download__badges">
      {storeBadges.map((badge) =>
        isDesktop ? (
          <span key={badge.label} className="auth-native-app-download__badge">
            <img src={badge.badgeSrc} alt={badge.label} loading="lazy" />
          </span>
        ) : (
          <a
            key={badge.label}
            href={badge.href}
            target="_blank"
            rel="noreferrer"
            aria-label={badge.label}
            className="auth-native-app-download__badge auth-native-app-download__badge--link"
          >
            <img src={badge.badgeSrc} alt={badge.label} loading="lazy" />
          </a>
        ),
      )}
    </div>
  );

  const qrBlock = (
    <div className="auth-native-app-download__qr" data-testid="auth-native-app-qr">
      <QrBlock showQr={isQrOk} onQrError={onQrError} variant="modal" />
    </div>
  );

  const copyBlock = (
    <div className="auth-native-app-download__copy">
      <h2 className="auth-native-app-download__title">{AUTH_NATIVE_APP_DOWNLOAD_TITLE}</h2>
      <p className="auth-native-app-download__subtitle">{AUTH_NATIVE_APP_DOWNLOAD_SUBTITLE}</p>
    </div>
  );

  const qrPromoCard = (
    <div className="auth-native-app-download__card">
      <div className="auth-native-app-download__body">
        {copyBlock}
        <hr className="auth-native-app-download__divider" />
        {qrBlock}
      </div>
      <div className="auth-native-app-download__footer">
        <div className="auth-native-app-download__scan-copy">
          <span className="auth-native-app-download__scan-title">{NATIVE_APP_PROMO_SCAN_TO_DOWNLOAD}</span>
          <span className="auth-native-app-download__scan-helper">{NATIVE_APP_PROMO_POINT_CAMERA_HELPER}</span>
        </div>
        {badges}
      </div>
    </div>
  );

  const loCard =
    isWithLo && loanOfficer ? (
      <div className="auth-native-app-download__lo-card" data-testid="auth-native-app-lo-card">
        <div className="auth-native-app-download__lo-identity">
          <div className="auth-native-app-download__lo-photo">
            {loanOfficer.photoImageUrl && !imageLoadError ? (
              <img
                src={loanOfficer.photoImageUrl}
                alt={loanOfficer.fullName}
                onError={() => {
                  setImageLoadError(true);
                  onHeadshotError?.();
                }}
              />
            ) : (
              <img src={NoLOIcon} alt="No loan officer" />
            )}
          </div>
          <div className="auth-native-app-download__lo-meta">
            <span
              className={`auth-native-app-download__lo-name ${GLOBAL_LINK_NO_TX_CLASS}`}
              data-testid="lo-officer-name"
            >
              {loanOfficer.fullName}
            </span>
            {title && <span className="auth-native-app-download__lo-title">{title}</span>}
            {loanOfficer.nmlsLicenseNumber && (
              <span className="auth-native-app-download__lo-nmls">{`NMLS #${loanOfficer.nmlsLicenseNumber}`}</span>
            )}
          </div>
        </div>
        <hr className="auth-native-app-download__lo-divider" />
        <div className="auth-native-app-download__lo-contact">
          {phoneNumber && (
            <a
              href={`tel:${phoneNumber}`}
              onClick={onPhoneClick}
              className="auth-native-app-download__lo-contact-row"
            >
              <FontAwesomeIcon icon={light("phone")} aria-hidden />
              <span className={GLOBAL_LINK_NO_TX_CLASS} data-testid="lo-officer-phone">
                {`+1 ${formatPhoneNumber(phoneNumber)}`}
              </span>
            </a>
          )}
          {email && (
            <a
              href={`mailto:${email}`}
              onClick={onEmailClick}
              className="auth-native-app-download__lo-contact-row"
            >
              <img src={EnvelopeIcon} alt="" aria-hidden />
              <span className={GLOBAL_LINK_NO_TX_CLASS} data-testid="lo-officer-email">
                {email}
              </span>
            </a>
          )}
        </div>
      </div>
    ) : null;

  const mobilePromoCard = (
    <div className="auth-native-app-download__card">
      {copyBlock}
      <button
        type="button"
        className="auth-native-app-download__get-app"
        onClick={openNativeAppStore}
        aria-label={NATIVE_APP_PROMO_GET_APP_BUTTON_LABEL}
      >
        {NATIVE_APP_PROMO_GET_APP_BUTTON_LABEL}
      </button>
      {badges}
    </div>
  );

  if (!isDesktop) {
    if (isWithLo) {
      return (
        <aside
          className="auth-native-app-download auth-native-app-download--mobile auth-native-app-download--with-lo"
          aria-label="Download the NAF Hub app"
        >
          {loCard}
          {mobilePromoCard}
        </aside>
      );
    }

    return (
      <aside className="auth-native-app-download auth-native-app-download--mobile" aria-label="Download the NAF Hub app">
        {mobilePromoCard}
      </aside>
    );
  }

  if (isWithLo) {
    return (
      <aside
        className="auth-native-app-download auth-native-app-download--with-lo"
        aria-label="Download the NAF Hub app"
      >
        {loCard}
        {qrPromoCard}
      </aside>
    );
  }

  return (
    <aside className="auth-native-app-download" aria-label="Download the NAF Hub app">
      {qrPromoCard}
    </aside>
  );
}
